/**
 * THE STATUS PLATE: one result, shown large (#2849, ROOT c.116345).
 *
 * A check-in or validation surface answers one question per scan or entry
 * ("valid?", "recorded?") and must be readable at arm's length. No kit piece
 * drew that: Notice is a row beside a working surface, ErrorState is a load
 * failure, Toast closes itself, Pill is a word (the mapping in the #2849
 * thread). So the kit grows ONE piece with three faces and ONE paint:
 *
 *   statusPlate.css   the only paint. A stable-class/data-attribute contract
 *                     (`.solum-status-plate`, `data-tone`, `data-slot`, ...),
 *                     not a CSS module, so markup written without React and
 *                     without a bundler can carry it. Published as
 *                     `@solum/browser-client/status-plate.css`.
 *   this file         the STRUCTURE (`statusPlateTree`) and the framework-free
 *                     DOM face (`createStatusPlate`). No React, no CSS import:
 *                     `@solum/browser-client/status-plate` is this file.
 *   StatusPlate.tsx   the React face. It renders the SAME tree from the SAME
 *                     function, so the two faces cannot drift. It has no
 *                     toggle: a React host puts `SegmentedControl`, the kit's
 *                     single-choice control, beside the plate.
 *
 * WHAT THE KIT OWNS, AND WHAT IT DOES NOT. The kit owns the material, the
 * type, the layout and the presentation states (tone, stale, long text,
 * reduced motion, forced colours). The HOST owns every decision: which tone a
 * result is, every string, the clock (when a result goes stale), the counts,
 * the direction and what pressing it does. There is no state machine here, no
 * timer and no storage.
 *
 * TEXT IS TEXT. Every host string reaches the page through `textContent` (the
 * DOM face) or as a React text child, and the toggle's group name through
 * `setAttribute`. No host value is parsed as HTML or CSS, and no host value
 * becomes a class: tones and roles are closed sets, and an unknown one falls
 * back to `idle` / `status` rather than landing in an attribute the paint does
 * not know.
 *
 * ONE SKELETON. The tree has the same elements for every view; an empty slot
 * is `hidden`, not absent. That is what lets the DOM face build its nodes once
 * and then only set attributes and text, so the live region exists before the
 * first result and a focused toggle keeps focus across updates.
 */

import type { Tone } from './Pill'

/** The kit's `Tone` (Pill), plus the two presentations a result plate needs:
 *  `idle` (no result to report: ready, or a recorded fact with no verdict) and
 *  `failure` (the result could not be produced, e.g. no connection). `brand`
 *  paints the host's `--brand` / `--brand-contrast`, a pair the host must have
 *  checked; the kit's own positive is `good`. */
export type StatusPlateTone = Tone | 'idle' | 'failure'

export const STATUS_PLATE_TONES: readonly StatusPlateTone[] = Object.freeze([
  'good', 'warn', 'critical', 'neutral', 'brand', 'idle', 'failure',
])

/** One option of the two-way toggle. `value` is the host's own key. */
export interface StatusPlateOption {
  value: string
  label: string
}

export interface StatusPlateView {
  tone: StatusPlateTone
  /** How the result is announced: `status` (polite, the default), `alert`
   *  (assertive, for a refusal the operator must not miss), or `off` for a host
   *  that announces results itself (one announcer, not two). The region is
   *  rewritten only when its words change: a new headline (a decay to "Ready"
   *  included) is announced; the same words again, or `stale` alone, are not. */
  live?: 'status' | 'alert' | 'off'
  /** The display word. Long or translated words step down a size on their own
   *  (see `isLongHeadline`). */
  headline: string
  /** A mark that repeats the tone without colour (✓ ✕ ? ...). Never announced. */
  glyph?: string
  detail?: string
  /** Who decided, in the host's words. */
  attribution?: string
  /** A small line announced with the result: an earlier outcome ("Earlier
   *  decision: OK"), or the last one after a decay ("Last: OK"). */
  note?: string
  /** "3 s ago". Small type outside the announced region, so a ticking age is
   *  not re-read every second. */
  age?: string
  /** A line that stays under the plate whatever the result is (a boundary or
   *  trust statement). Outside the announced region. */
  footnote?: string
  /** The host's clock says this result is old: the plate drops its fill, keeps
   *  the words and the tone's mark, and steps the headline, glyph and
   *  attribution down. */
  stale?: boolean
  /** A header row above the plate. */
  head?: { title: string; chip?: string; liveLabel?: string; count?: string }
  /** The DOM face only: a two-way pressed toggle below the plate (In | Out),
   *  for a page that cannot render `SegmentedControl`. The host owns the
   *  value; pressing calls the host back and changes nothing by itself. */
  toggle?: { label: string; value: string; options: readonly [StatusPlateOption, StatusPlateOption] }
  /** The least share of the viewport's height the WHOLE plate takes, 0 to 1:
   *  the head, footnote and toggle keep their height and the face takes the
   *  rest (1, on a plate at the top of the page, fills the screen). */
  fill?: number
}

/** One element of the structure. `vars` are custom properties on `style`. */
export interface StatusPlateNode {
  tag: 'div' | 'p' | 'span' | 'button'
  attrs: Readonly<Record<string, string>>
  vars?: Readonly<Record<string, string>>
  text?: string
  children?: readonly StatusPlateNode[]
}

/**
 * When the headline takes the smaller display step: a word longer than six
 * characters, or more than sixteen in all. Measured against the 64px floor of
 * `--fs-display` in a 360px phone's plate (about 288px of line): an upper-case
 * heavy word of seven characters ("WARNUNG", 357px) does not fit on one line
 * at that size, and a sentence of more than sixteen ("NOT VALID FOR THIS
 * EVENT") would take four lines of it. Counted in code points, so "GÜLTIG" is six.
 */
export function isLongHeadline(headline: string): boolean {
  const words = headline.split(/\s+/u)
  return [...headline].length > 16 || words.some((word) => [...word].length > 6)
}

const text = (value: unknown): string => (value == null ? '' : String(value))
const slot = (name: string, extra: Record<string, string> = {}, shown = true) =>
  ({ 'data-slot': name, ...extra, ...(shown ? {} : { hidden: '' }) })
const leaf = (tag: StatusPlateNode['tag'], name: string, value: unknown, extra: Record<string, string> = {}): StatusPlateNode => {
  const content = text(value)
  return { tag, attrs: slot(name, extra, content !== ''), text: content }
}

/** The structure every face renders: the same elements for every view. */
export function statusPlateTree(view: StatusPlateView): StatusPlateNode {
  const tone = STATUS_PLATE_TONES.includes(view.tone) ? view.tone : 'idle'
  const live = view.live === 'alert' || view.live === 'off' ? view.live : 'status'
  const headline = text(view.headline)
  const head = view.head
  const toggle = view.toggle
  const fill = typeof view.fill === 'number' && Number.isFinite(view.fill) ? Math.min(1, Math.max(0, view.fill)) : 0
  const option = (index: 0 | 1): StatusPlateNode => {
    const chosen = toggle?.options[index]
    return {
      tag: 'button',
      attrs: {
        type: 'button',
        'data-value': text(chosen?.value),
        'aria-pressed': chosen != null && chosen.value === toggle?.value ? 'true' : 'false',
      },
      text: text(chosen?.label),
    }
  }
  return {
    tag: 'div',
    attrs: {
      class: 'solum-status-plate',
      'data-tone': tone,
      ...(isLongHeadline(headline) ? { 'data-length': 'long' } : {}),
      ...(view.stale ? { 'data-stale': '' } : {}),
    },
    ...(fill > 0 ? { vars: { '--status-plate-fill': String(fill) } } : {}),
    children: [
      {
        tag: 'div',
        attrs: slot('head', {}, head != null),
        children: [
          leaf('span', 'title', head?.title),
          leaf('span', 'chip', head?.chip),
          leaf('span', 'live', head?.liveLabel),
          leaf('span', 'count', head?.count),
        ],
      },
      {
        tag: 'div',
        attrs: slot('face'),
        children: [
          leaf('span', 'glyph', view.glyph, { 'aria-hidden': 'true' }),
          {
            tag: 'div',
            attrs: slot('message', live === 'off' ? {} : { role: live, 'aria-atomic': 'true' }),
            children: [
              leaf('p', 'headline', headline),
              leaf('p', 'detail', view.detail),
              leaf('p', 'attribution', view.attribution),
              leaf('p', 'note', view.note),
            ],
          },
          leaf('p', 'age', view.age),
        ],
      },
      leaf('p', 'footnote', view.footnote),
      {
        tag: 'div',
        attrs: slot('toggle', toggle != null ? { role: 'group', 'aria-label': text(toggle.label) } : { role: 'group' }, toggle != null),
        children: [option(0), option(1)],
      },
    ],
  }
}

export interface StatusPlateOptions {
  /** Called with the pressed option's `value`. The plate does not press itself:
   *  the host decides, then calls `update` with the new `toggle.value`. */
  onToggle?: (value: string) => void
}

export interface StatusPlateHandle {
  readonly element: HTMLElement
  update(view: StatusPlateView): void
  destroy(): void
}

function build(doc: Document, node: StatusPlateNode): HTMLElement {
  const element = doc.createElement(node.tag)
  for (const [name, value] of Object.entries(node.attrs)) element.setAttribute(name, value)
  for (const [name, value] of Object.entries(node.vars ?? {})) element.style.setProperty(name, value)
  if (node.children) for (const child of node.children) element.appendChild(build(doc, child))
  else element.textContent = node.text ?? ''
  return element
}

function sync(element: HTMLElement, was: StatusPlateNode, now: StatusPlateNode): void {
  for (const name of Object.keys(was.attrs)) if (!(name in now.attrs)) element.removeAttribute(name)
  for (const [name, value] of Object.entries(now.attrs)) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value)
  }
  for (const name of Object.keys(was.vars ?? {})) if (!now.vars || !(name in now.vars)) element.style.removeProperty(name)
  for (const [name, value] of Object.entries(now.vars ?? {})) {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value)
  }
  if (now.children) {
    now.children.forEach((child, i) => sync(element.children[i] as HTMLElement, (was.children as StatusPlateNode[])[i]!, child))
  } else if (element.textContent !== (now.text ?? '')) {
    // Only when it changed: rewriting the same words would re-announce them.
    element.textContent = now.text ?? ''
  }
}

/**
 * The framework-free face: builds the plate once inside `mount`, then `update`
 * only sets attributes and text. Mount it before the first result, so the
 * announced region exists when the result arrives.
 */
export function createStatusPlate(mount: Element, view: StatusPlateView, options: StatusPlateOptions = {}): StatusPlateHandle {
  const doc = mount.ownerDocument
  let tree = statusPlateTree(view)
  const element = build(doc, tree)
  const onClick = (event: Event) => {
    const target = event.target as Element | null
    const button = target?.closest?.('button[data-value]')
    if (button && element.contains(button) && button.parentElement?.getAttribute('data-slot') === 'toggle') {
      options.onToggle?.(button.getAttribute('data-value') ?? '')
    }
  }
  element.addEventListener('click', onClick)
  mount.appendChild(element)
  return {
    element,
    update(next) {
      const now = statusPlateTree(next)
      sync(element, tree, now)
      tree = now
    },
    destroy() {
      element.removeEventListener('click', onClick)
      element.remove()
    },
  }
}
